import { Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { statusTone } from './call-status-tone';

/**
 * A late-cancellation fee was accepted on this call (ADR-290).
 *
 * Red, beside the status pill, because it is the one thing on the call a
 * billing person must act on: the patient agreed to a charge and nothing
 * posts it for them. Same red as the `needs_you` status pill so red means one
 * thing across the row.
 *
 * ── Source of truth ──────────────────────────────────────────────────────
 * The APPOINTMENT RECORD, derived per read on `GET /api/interactions/:id`
 * (`scheduled_appointments.cancelled_interaction_id`) — not the evaluation,
 * which is null until the post-call evaluator runs. So it shows on a call that
 * was never evaluated. A fee the caller declined writes nothing and shows
 * nothing: the appointment was kept and there is nothing to bill.
 */

/** "$50" for whole dollars, "$12.50" otherwise. */
function formatFeeCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** What the pill should say, or `null` when no fee was accepted on the call. */
export function cancellationFeePillFor(
  cancellationFeeCents: number | null | undefined
): { label: string; title: string } | null {
  if (typeof cancellationFeeCents !== 'number' || !(cancellationFeeCents > 0)) return null;
  return {
    label: 'Cancellation fee',
    title: `Patient accepted a ${formatFeeCents(cancellationFeeCents)} late-cancellation fee`,
  };
}

interface CancellationFeePillProps {
  /** From `GET /api/interactions/:id` — the appointment record, not the evaluation. */
  cancellationFeeCents: number | null | undefined;
  className?: string;
}

export function CancellationFeePill({
  cancellationFeeCents,
  className,
}: CancellationFeePillProps): React.ReactElement | null {
  const pill = cancellationFeePillFor(cancellationFeeCents);
  if (!pill) return null;
  return (
    <Badge
      variant="outline"
      title={pill.title}
      aria-label={pill.title}
      className={cn('gap-1 shrink-0', statusTone('needs_you')?.pill, className)}
    >
      <Receipt className="h-3 w-3" />
      {pill.label}
    </Badge>
  );
}
